"use client";
import React, { useMemo, useState } from "react";
import { BadgePercent, Edit3Icon, Loader2, Plus, Trash2, X, Search, LayoutList, CheckCircle2, XCircle } from "lucide-react";
import Pagination, { usePagination } from '../shared/Pagination';
import { createPortal } from "react-dom";
import { useExpenses } from "../context/ExpenseContext";

/* ================= MAIN PAGE ================= */

export default function ExpensesPage() {
  const {
    expenses,
    loading,
    addExpense,
    updateExpense,
    toggleStatus,
    deleteExpense,
  } = useExpenses();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false); // <-- new

  /* ---------- FILTER & SORT ---------- */
  const filtered = useMemo(() => {
    let data = expenses.filter((d) =>
      d.name.toLowerCase().includes(search.toLowerCase())
    );

    if (sortBy === "az") data.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "za") data.sort((a, b) => b.name.localeCompare(a.name));
    if (sortBy === "status") data.sort((a, b) =>
      a.status.localeCompare(b.status)
    );

    return data;
  }, [expenses, search, sortBy]);

  /* ---------- ACTIONS ---------- */

  const save = async () => {
    if (!name.trim() || saving) return; // guard against double-click

    const payload = {
      name,
      description,
    };

    setSaving(true);
    try {
      if (editing) {
        await updateExpense(editing.id, payload);
      } else {
        await addExpense(payload);
      }

      setModalOpen(false);
      setEditing(null);
      setName("");
      setDescription("");
    } catch (err) {
      console.error("Failed to save expense:", err);
      // optionally surface an error toast/message here
    } finally {
      setSaving(false);
    }
  };

  const { page: exPage, pageSize: exSize, setPage: setExPage, setPageSize: setExSize, totalPages: exTotalPages, totalItems: exTotal, paginated: paginatedExp } = usePagination(filtered, 10);

  const toggleStatusHandler = (id, status) => {
    toggleStatus(id, status);
  };

  const removeHandler = (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this expense?"
    );

    if (confirmDelete) {
      deleteExpense(id);
    }
  };

  const closeModal = () => {
    if (saving) return; // don't allow closing mid-save
    setModalOpen(false);
  };

  /* ================= UI ================= */

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-5">

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Total Expenses" value={expenses.length} icon={LayoutList} accent="border-l-teal-500" color="text-teal-700" />
        <Stat label="Active" value={expenses.filter(d => d.status === "active").length} icon={CheckCircle2} accent="border-l-green-500" color="text-green-600" />
        <Stat label="Inactive" value={expenses.filter(d => d.status === "inactive").length} icon={XCircle} accent="border-l-red-400" color="text-red-500" />
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Search expenses…"
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:bg-white transition"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 min-w-[140px]"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="">Sort by name</option>
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
        </select>
        <span className="flex items-center text-xs text-gray-400 px-1 whitespace-nowrap">
          {filtered.length} of {expenses.length} records
        </span>
        <button onClick={() => { setModalOpen(true); setEditing(null); setName(""); setDescription(""); }}
          className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0">
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Expense</span>
        </button>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-teal-700 text-white">
            <tr className="bg-teal-700 text-white">
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">
                Expense Name
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-400 text-sm">
                  No expenses found
                </td>
              </tr>
            ) : (
              paginatedExp.map((item, idx) => (
                <tr key={item.id} className={`transition-colors hover:bg-teal-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                  <td className="px-6 py-4 text-xs text-gray-400">{idx + 1}</td>

                  <td className="px-6 py-4 font-medium">{item.name}</td>

                  <td className="px-6 py-4 text-gray-600">
                    {item.description}
                  </td>

                  <td className="px-6 py-4">
                    <Toggle
                      active={item.status === "active"}
                      onToggle={() => toggleStatusHandler(item.id, item.status)}
                    />
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => { setEditing(item); setName(item.name); setDescription(item.description || ""); setModalOpen(true); }}
                        className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
                      ><Edit3Icon size={15} /></button>
                      <button
                        onClick={() => removeHandler(item.id)}
                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                      ><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {exTotalPages > 1 && (
          <div className="px-4 border-t border-gray-100">
            <Pagination currentPage={exPage} totalPages={exTotalPages} totalItems={exTotal}
              pageSize={exSize} onPageChange={setExPage} onPageSizeChange={setExSize} />
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400 text-sm">
            No expenses found
          </div>
        ) : (
          paginatedExp.map((item, idx) => (
            <div key={item.id} className={`bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3 ${idx % 2 !== 0 ? "bg-gray-50/40" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">ID{idx + 1}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditing(item); setName(item.name); setDescription(item.description || ""); setModalOpen(true); }}
                    className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
                  ><Edit3Icon size={15} /></button>
                  <button
                    onClick={() => removeHandler(item.id)}
                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                  ><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="font-semibold text-gray-800">{item.name}</p>
              {item.description && <p className="text-sm text-gray-500">{item.description}</p>}
              <Toggle active={item.status === "active"} onToggle={() => toggleStatusHandler(item.id, item.status)} />
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {modalOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal} />
            <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col overflow-hidden">

              {/* Header */}
              <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white tracking-tight">
                  {editing ? "Edit Expense" : "Add New Expense"}
                </h2>
                <button onClick={closeModal} disabled={saving}
                  className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors disabled:opacity-50">
                  <X size={16} className="text-white" />
                </button>
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Expense Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Office Supplies"
                    maxLength={50}
                    disabled={saving}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm disabled:bg-gray-50 transition-colors"
                  />
                  <p className="text-xs text-gray-400 text-right">{name.length}/50</p>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description (optional)…"
                    maxLength={200}
                    disabled={saving}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm resize-none disabled:bg-gray-50 transition-colors"
                  />
                  <p className="text-xs text-gray-400 text-right">{description.length}/200</p>
                </div>
{/* 
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                    {["active", "inactive"].map(s => (
                      <button key={s} type="button" onClick={() => setStatus(s)} disabled={saving}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium capitalize transition-all disabled:opacity-50 ${
                          status === s
                            ? s === "active" ? "bg-white text-green-700 shadow-sm ring-1 ring-green-200" : "bg-white text-red-600 shadow-sm ring-1 ring-red-200"
                            : "text-gray-500 hover:text-gray-700"
                        }`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div> */}

                <div className="flex gap-3 pt-4 border-t border-gray-100">
                  <button type="button" onClick={closeModal} disabled={saving}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50">
                    Cancel
                  </button>
                  <button type="button" onClick={save} disabled={saving || !name.trim()}
                    className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-40 transition-colors shadow-sm flex items-center justify-center gap-2">
                    {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : (editing ? "Update Expense" : "Add Expense")}
                  </button>
                </div>
              </div>

            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const Stat = ({ label, value, icon: Icon, accent, color }) => (
  <div className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
    <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 leading-tight">{label}</p>
    </div>
  </div>
);

const Toggle = ({ active, onToggle }) => (
  <button onClick={onToggle}
    className={`relative inline-flex h-5 w-10 flex-shrink-0 items-center rounded-full transition-colors ${active ? "bg-green-500" : "bg-gray-300"}`}>
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${active ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
      status === "active"
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"
    }`}
  >
    {status}
  </span>
);

const Actions = ({ item, onEdit, onToggle, onDelete }) => {
  return (
    <div className="flex items-center gap-3 justify-end">
      <button
        onClick={() => onToggle(item.id, item.status)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full ${
          item.status === "active" ? "bg-green-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white ${
            item.status === "active" ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>

      <button onClick={onEdit} className="text-teal-600">
        <Edit3Icon size={16} />
      </button>

      <button onClick={() => onDelete(item.id)} className="text-red-500">
        <Trash2 size={16} />
      </button>
    </div>
  );
};